import { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_LOADER_TIMEOUT } from './constants'

interface LoaderState {
  show: boolean
  bg?: string
}

interface Model {
  id: string
  loader?: {
    bg?: string
  }
}

interface UseStaticModelLoaderArgs {
  models: Model[]
}

const useStaticModelLoader = ({ models = [] }: UseStaticModelLoaderArgs) => {
  const [loaderState, setLoaderState] = useState<LoaderState>({
    show: false,
    bg: '',
  })
  const loaderTimeoutRef = useRef<NodeJS.Timeout>()

  const showLoader = !!(loaderState.bg && loaderState.show)
  const loaderBg = loaderState.bg

  const findBg = useCallback(
    (modelId: string) => {
      const loadableModel = models?.find((model) => model.id === modelId)
      return loadableModel?.loader?.bg
    },
    [models]
  )

  const resetTimer = useCallback(() => {
    if (loaderTimeoutRef.current) {
      clearTimeout(loaderTimeoutRef.current)
    }
  }, [])

  const setLoaderTimeout = useCallback<(timeout?: number) => void>(
    (timeout = DEFAULT_LOADER_TIMEOUT) => {
      resetTimer()
      loaderTimeoutRef.current = setTimeout(() => {
        setLoaderState({
          show: false,
          bg: '',
        })
      }, timeout)
    },
    [resetTimer]
  )

  const startLoader = useCallback(
    (loadableModelId: string, timeout?: number) => {
      const bg = findBg(loadableModelId)
      if (!bg) {
        return
      }
      setLoaderState({
        show: true,
        bg,
      })
      setLoaderTimeout(timeout)
    },
    [findBg, setLoaderTimeout]
  )

  useEffect(
    () => () => {
      resetTimer()
    },
    [resetTimer]
  )

  return {
    showLoader,
    loaderBg,
    startLoader,
  }
}

export default useStaticModelLoader
